import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {
    AppleIcon,
    launchAppleConnection,
    linkAppleAccount,
} from "@khan/auth-apple";
import {createReauthUrl, type LoginType} from "@khan/auth-shared";
// eslint-disable-next-line no-restricted-imports -- TODO(FEI-7051): switch to useGql
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {redirect, type Location} from "@khan/urls";

import RequireReauth from "../require-reauth.tsx";

import ResponsiveLinkedAccountRow from "./responsive-linked-account-row.tsx";

interface Props {
    appleId: string | null | undefined;
    applePrimaryEmail: string | null | undefined;
    facebookId: string | null | undefined;
    microsoftId: string | null | undefined;
    googlePrimaryEmail: string | null | undefined;
    gqlClient: ApolloGqlClient;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    location: Location;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    setWarningMessage: (message: string | null) => void;
}

export const LinkAppleAccount: React.FC<Props> = ({
    gqlClient,
    appleId,
    applePrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    googlePrimaryEmail,
    facebookId,
    microsoftId,
    renderThirdPartyDisconnectButton,
    location,
}) => {
    const onConnectAppleLogin = () => {
        launchAppleConnection(gqlClient, "LINK", location.pathname).then(
            async (response) => {
                // When we sign in using native sign-in (iOS/Safari), the
                // browser automatically redirects to Apple and then
                // redirects back. We hit this code as the redirect starts so
                // there's nothing to do here.
                if (response.nativeSignIn) {
                    return null;
                }

                // Complete the link flow.
                try {
                    await linkAppleAccount(gqlClient, {
                        authCode: response.authorization.code,
                        state: response.authorization.state,
                    });
                    // get redirect URL that will display the success
                    // message.
                    const reauthUrl = createReauthUrl({
                        loginType: "apple",
                        pathname: location.pathname,
                    });
                    redirect(reauthUrl);
                } catch (error: any) {
                    Log.error("Sign in with Apple failed", Errors.Internal, {
                        cause: error,
                    });
                    // get redirect URL that will display the error message.
                    const reauthUrlError = createReauthUrl({
                        loginType: "apple",
                        pathname: location.pathname,
                        error: error.message,
                    });

                    redirect(reauthUrlError);
                }
            },
        );
    };

    const connectAppleButton = (
        <RequireReauth
            identifier={identifier}
            showClasslinkAuthProvider={isClasslinkUser}
            onSuccess={onConnectAppleLogin}
            onFailure={() => {}}
            hasAppleButton={false}
            // We check the other providers if the user has an account
            // registered with them
            hasGoogleButton={!!googlePrimaryEmail}
            hasFacebookButton={!!facebookId}
            hasMicrosoftButton={!!microsoftId}
        >
            {(invokeReauth) => (
                <Button
                    onClick={() => {
                        setWarningMessage(null);
                        invokeReauth();
                    }}
                    size="small"
                    kind="tertiary"
                >
                    {t`Connect with Apple`}
                </Button>
            )}
        </RequireReauth>
    );

    return (
        <ResponsiveLinkedAccountRow
            icon={<AppleIcon ariaHidden={true} />}
            label={doNotTranslate`Apple`}
            content={appleId ? applePrimaryEmail : undefined}
            action={
                appleId
                    ? renderThirdPartyDisconnectButton("apple")
                    : connectAppleButton
            }
        />
    );
};

export default LinkAppleAccount;
